import { createElement } from 'react';
import { Components, Theme } from '@mui/material';
import LelyIconButton from '@/common/components/muiWrappers/LelyIconButton/LelyIconButton';

/** Forwards MUI's icon / iconOpen className so position + open rotation apply. */
function SelectIndicator({ className }: { className?: string }) {
  return createElement(LelyIconButton, {
    icon: 'lely-icon-caret-down',
    size: 'lg',
    className,
    tabIndex: -1,
    disableRipple: true,
    'aria-hidden': true,
    sx: {
      padding: 0,
      pointerEvents: 'none',
      // Keep caret color from Select icon slot (Icon.scss hardcodes otherwise)
      color: 'inherit',
      '& .icon-svg': {
        color: 'currentColor',
      },
    },
  });
}

export const getSelectComponents = (
  theme: Theme,
): Pick<Components<Theme>, 'MuiSelect' | 'MuiMenuItem'> => {
  const fieldPaddingX = theme.lelySpacing.MD;
  const iconSize = theme.lelySpacing.XL;
  const iconGap = theme.lelySpacing.SM;
  const selectIconOffset = `calc(${fieldPaddingX} + ${iconSize} + ${iconGap})`;

  return {
    MuiSelect: {
      defaultProps: {
        IconComponent: SelectIndicator,
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 240,
            },
          },
        },
      },
      styleOverrides: {
        select: {
          paddingRight: selectIconOffset,

          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
        icon: {
          color: theme.palette.text.subtle,
          position: 'absolute',
          right: fieldPaddingX,
          top: '50%',
          transform: 'translateY(-50%)',
          width: iconSize,
          height: iconSize,
        },
        iconOpen: {
          transform: 'translateY(-50%) rotate(180deg)',
        },
        root: {
          '& .MuiInputAdornment-positionEnd': {
            marginLeft: iconGap,
            marginRight: selectIconOffset,
          },
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          ...theme.typography.xs,
          padding: `${theme.lelySpacing.SM} ${theme.lelySpacing.MD}`,

          '&:hover': {
            backgroundColor: theme.palette.background.secondaryHover,
          },

          '&.Mui-selected': {
            backgroundColor: theme.palette.background.secondaryActive,

            '&:hover': {
              backgroundColor: theme.palette.background.secondaryHover,
            },
          },
        },
      },
    },
  };
};
